import { useOpacityToggle } from '@hooks'
import { useAmuseBoostStore, useJackpotStore, useNavigationStore, useSpinButtonSectionStore } from '@stores'
import React, { useEffect, useRef } from 'react'
import JackpotCardsSection from './JackpotCards/JackpotCardsSection'
import JackpotLinkSection from './JackpotLink/JackpotLinkSection'
import {
    ChangeOpacityComponents,
    EQLevelReachedMessage,
    GameUIMessageScope,
    GameUIMessageType,
    JackpotFreezeIndexMessage,
    JackpotPhase,
    JackpotStatisticsMessage,
    JackpotType,
    JackpotWinAnimationStartMessage,
} from 'game-ui-types'
import JackpotEQSection from './JackpotEQ/JackpotEQSection'

const JackpotSection: React.FC = () => {
    const jackpotType = useJackpotStore((state) => state.jackpotType)
    const setJackpotCardsStatistics = useJackpotStore((state) => state.setJackpotCardsStatistics)
    const setJackpotEQStatistics = useJackpotStore((state) => state.setJackpotEQStatistics)
    const setJackpotLinkStatistics = useJackpotStore((state) => state.setJackpotLinkStatistics)
    const setJackpotPhase = useJackpotStore((state) => state.setJackpotPhase)
    const setIsJackpotEQStarted = useJackpotStore((state) => state.setIsJackpotEQStarted)
    const resetPickedSuits = useJackpotStore((state) => state.resetPickedSuits)
    const setFreezeIndex = useJackpotStore((state) => state.setFreezeIndex)
    const setHasLeadElement = useJackpotStore((state) => state.setHasLeadElement)
    const setJackpotStatsCountdownToZeroDurationSeconds = useJackpotStore(
        (state) => state.setJackpotStatsCountdownToZeroDurationSeconds
    )
    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((state) => state.isAutoplaySectionOpened)

    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)
    const isAmuseBoostModalOpen = useAmuseBoostStore((state) => state.isAmuseBoostModalOpen)

    const jackpotSectionComponent = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.JACKPOT_SECTION, jackpotSectionComponent)

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, scope } = rawData

            switch (type) {
                case GameUIMessageType.JACKPOT_STATISTICS: {
                    const data = rawData as JackpotStatisticsMessage

                    if (data.jackpotCardStatistics) {
                        setJackpotCardsStatistics(data.jackpotCardStatistics)
                    }

                    if (data.jackpotEQStatistics) {
                        setJackpotEQStatistics(data.jackpotEQStatistics)
                    }

                    if (data.jackpotLinkStatistics) {
                        setJackpotLinkStatistics(data.jackpotLinkStatistics)
                    }

                    break
                }
                case GameUIMessageType.JACKPOT_STARTED: {
                    setJackpotPhase(JackpotPhase.INTRO_STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_RUNNING: {
                    setJackpotPhase(JackpotPhase.RUNNING)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_STARTED: {
                    setIsJackpotEQStarted(true)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_ENDED: {
                    setIsJackpotEQStarted(false)
                    resetPickedSuits()
                    setFreezeIndex(-1)
                    setJackpotPhase(JackpotPhase.CLOSED)
                    break
                }
                case GameUIMessageType.EQ_LEVEL_REACHED: {
                    const data = rawData as EQLevelReachedMessage

                    setFreezeIndex(data.levelIndex)
                    break
                }
                case GameUIMessageType.JACKPOT_FREEZE_INDEX: {
                    const data = rawData as JackpotFreezeIndexMessage

                    setFreezeIndex(data.freezeIndex)
                    break
                }
                case GameUIMessageType.JACKPOT_WIN_ANIMATION_START: {
                    const data = rawData as JackpotWinAnimationStartMessage

                    setJackpotStatsCountdownToZeroDurationSeconds(data.animationTime)
                    setJackpotPhase(JackpotPhase.OUTRO_STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_CLICKED_DURING_WIN_ANIMATION: {
                    setJackpotPhase(JackpotPhase.OUTRO_CLICKED)
                    break
                }
                case GameUIMessageType.JACKPOT_WIN_ANIMATION_COMPLETE: {
                    // End of results should behave like jackpot outro clicked phase
                    setJackpotPhase(JackpotPhase.OUTRO_CLICKED)
                    break
                }
                case GameUIMessageType.JACKPOT_COMPLETE: {
                    resetPickedSuits()
                    setJackpotPhase(JackpotPhase.CLOSED)
                    break
                }
            }

            if (scope !== GameUIMessageScope.JACKPOT && scope !== GameUIMessageScope.JACKPOT_CARDS) {
                return
            }

            switch (type) {
                case GameUIMessageType.JACKPOT_HAS_LEAD_ELEMENT: {
                    setHasLeadElement(true)
                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    let jackpotSection = <JackpotCardsSection />

    if (jackpotType === JackpotType.JACKPOT_LINK) {
        jackpotSection = <JackpotLinkSection />
    } else if (jackpotType === JackpotType.JACKPOT_EGYPT_QUEST) {
        jackpotSection = <JackpotEQSection />
    }

    return (
        <div
            ref={jackpotSectionComponent}
            className={`relative ${isBetSectionOpened || isAutoplaySectionOpened || isMenuOpen || isAmuseBoostModalOpen ? 'z-[0]' : 'z-[101]'}`}
        >
            {jackpotSection}
        </div>
    )
}

export default JackpotSection
